import {
  Box,
  Flex,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React, { useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { MdClose, MdMenu, MdOutlineSearch } from 'react-icons/md';
import { usePrevious } from 'react-use';

import { NonCommerceInfoContext } from '@rsc/contexts';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import hiddenMavenoid from '@rsc/ui-components/dept/utils/hiddenMavenoid';
import SearchInput from '@rsc/ui-components/dept/atoms/SearchInput';
import NavCartButton from '@rsc/ui-components/dept/atoms/NavCartButton';
import Link from '@rsc/ui-components/tailwind/Link';
import MobileMenu from './MobileMenu';
import NavAccountButton from '../../atoms/NavAccountButton';
import NotificationCarousel from '../NotificationCarousel';

const menuHeightNoBanner = 112;

export default function MobileNav({
  menuItems,
  notificationBanner,
  bannerShowing,
}) {
  const { isNonCommerce } = useContext(NonCommerceInfoContext);
  const { tt } = useUiComponentsInternationalContext();

  const theme = useTheme();
  const [isLgScreen] = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, {
    ssr: true,
  });
  const [searchFocusUpdater, setSearchFocusUpdater] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollYRef = useRef(null);

  const [navShowing, setNavShowing] = useState(true);
  const [atTopOfPage, setAtTopOfPage] = useState(true);
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const updateShowNav = () => {
      if (menuOpen) {
        setNavShowing(true);
        return;
      }

      const distanceFromTopNavStartsBeingHidden = 80;
      const topOfPageMark = distanceFromTopNavStartsBeingHidden + 1;
      const scrollSensitivity = 2;
      const { scrollY } = window;
      const shouldShowNav = !(
        scrollY > lastScrollY &&
        window.scrollY > distanceFromTopNavStartsBeingHidden
      );

      const scrollDelta = scrollY - lastScrollY;

      if (
        // If the state needs to be changed and the scroll delta is greater than 2
        shouldShowNav !== navShowing &&
        (scrollDelta > scrollSensitivity || scrollDelta < -scrollSensitivity)
      ) {
        setNavShowing(shouldShowNav);
      }

      if (scrollY <= topOfPageMark && !atTopOfPage) {
        setAtTopOfPage(true);
        setNavShowing(true);
      } else if (scrollY >= topOfPageMark * 2 && atTopOfPage) {
        setAtTopOfPage(false);
      }

      lastScrollY = scrollY > 0 ? scrollY : 0;
    };

    updateShowNav();

    window.addEventListener('scroll', updateShowNav);
    return () => {
      window.removeEventListener('scroll', updateShowNav);
    };
  }, [navShowing, atTopOfPage, menuOpen]);

  useEffect(() => {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      // grab the gatsby wrapper rather than the body so that we limit background scrolling not nav scrolling
      const gatsbyFocusWrapper = document.getElementById(
        'gatsby-focus-wrapper'
      );
      if (menuOpen) {
        // Capture the scroll depth prior to opening the menu
        scrollYRef.current = window.scrollY;
        // Turn off background scrolling
        if (gatsbyFocusWrapper) {
          gatsbyFocusWrapper.style.overflow = 'hidden';
          gatsbyFocusWrapper.style.height = '1vh';
        }
      } else {
        gatsbyFocusWrapper.style.height = 'auto';
      }

      hiddenMavenoid(menuOpen);
    }
  }, [menuOpen]);

  const wasLgScreen = usePrevious(isLgScreen);
  useEffect(() => {
    if (!wasLgScreen && isLgScreen) {
      document.body.style.overflow = 'auto';
    }
  }, [isLgScreen, wasLgScreen]);

  const handleSearchIconClick = () => {
    setMenuOpen(!menuOpen);
    setSearchFocusUpdater(searchFocusUpdater + 1);
  };

  return (
    <>
      <Box
        display={{ base: 'block', lg: 'none' }}
        pos="fixed"
        top={navShowing ? '0px' : '-100px'}
        transition=".5s top"
        zIndex="modal"
      >
        {bannerShowing && (
          <NotificationCarousel
            notificationBanner={notificationBanner}
            atTopOfPage={atTopOfPage}
            menuOpen={menuOpen}
          />
        )}
        <Flex
          bg="brand.ink"
          w="100%"
          minW="100vw"
          h="14"
          py="2"
          px="4"
          overflow="hidden"
          justify="space-between"
          align="center"
        >
          <Box
            as={isNonCommerce ? 'span' : Link}
            backgroundImage={`url(${theme.images.yellowLogo})`}
            backgroundSize="cover"
            backgroundPosition="center"
            aria-label={tt(s => s.mobileNavComponent.petsafeLogoLabel)}
            w="131px"
            h="32px"
            link="/"
          />
          {!isNonCommerce && (
            <Flex justify="space-between" align="center">
              <IconButton
                icon={<MdOutlineSearch size={24} />}
                variant="nav"
                onClick={handleSearchIconClick}
                aria-label={tt(s => s.mobileNavComponent.openSearchLabel)}
                pos="relative"
                left="0px"
                transform={
                  menuOpen || atTopOfPage
                    ? `translateY(${menuHeightNoBanner / 2}px)`
                    : 'translateY(0px)'
                }
                transition="transform .3s ease-in-out"
              />

              <NavCartButton />
              <NavAccountButton />

              <IconButton
                onClick={() => setMenuOpen(!menuOpen)}
                icon={menuOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
                variant="nav"
                aria-label={tt(
                  s => s.mobileNavComponent.toggleNavigationMenuLabel
                )}
                color="typography.lighter"
              />
            </Flex>
          )}
        </Flex>
        {!isNonCommerce && (
          <Box
            bg="brand.ink"
            w="100%"
            overflow="hidden"
            as={motion.div}
            animate={{ height: atTopOfPage || menuOpen ? '56px' : '0px' }}
            px="4"
          >
            <SearchInput
              pb="4"
              toggleMobileMenu={setMenuOpen}
              searchFocusUpdater={searchFocusUpdater}
            />
          </Box>
        )}
      </Box>
      {!isNonCommerce && (
        <MobileMenu menuItems={menuItems} menuOpen={menuOpen} />
      )}
    </>
  );
}

MobileNav.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    })
  ).isRequired,
  notificationBanner: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  bannerShowing: PropTypes.bool.isRequired,
};
